import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import {
  MILESTONE_LABEL,
  getYesterdayRecap,
  clearYesterdayRecap,
  getDailyStreak,
} from "@/lib/dailyPoints";

/**
 * <DailyPointsMilestones /> — global overlay component. Renders two
 * things:
 *
 *   1. A full-screen milestone burst when the child crosses 25 / 50 /
 *      100 / 200 daily points. Listens to the `mwq-daily-points`
 *      window event dispatched by `addDailyPoints()`.
 *
 *   2. A one-off "Yesterday you earned X points!" recap card on the
 *      very first mount of a new day. Dismissible; auto-hides after 12
 *      seconds so it never blocks tap targets.
 *
 * Mounted once at the App shell level so every page can trigger it.
 */
export default function DailyPointsMilestones() {
  const [burst, setBurst] = useState(null);  // { milestone, total }
  const [recap, setRecap] = useState(null);  // yesterday's recap

  useEffect(() => {
    // Milestone burst — listen for the app-wide event.
    const onDaily = (e) => {
      const { milestone, total } = e.detail || {};
      if (!milestone) return;
      setBurst({ milestone, total });
      // Auto-close after 3.5 s so nothing stays over tap targets.
      window.setTimeout(() => setBurst((b) => (b?.milestone === milestone ? null : b)), 3500);
    };
    window.addEventListener("mwq-daily-points", onDaily);
    return () => window.removeEventListener("mwq-daily-points", onDaily);
  }, []);

  useEffect(() => {
    // Show yesterday's recap ONCE per day. localStorage flag prevents
    // repeat pop-ups within the same day.
    const r = getYesterdayRecap();
    if (!r) return;
    const shownKey = `mwq_daily_recap_shown_${r.date}`;
    if (localStorage.getItem(shownKey)) return;
    localStorage.setItem(shownKey, "1");
    setRecap(r);
    window.setTimeout(() => setRecap(null), 12000);
  }, []);

  const dismissRecap = () => {
    setRecap(null);
    clearYesterdayRecap();
  };

  return (
    <>
      <AnimatePresence>
        {burst && MILESTONE_LABEL[burst.milestone] && (
          <motion.div
            key={burst.milestone}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center"
            data-testid="daily-milestone-burst"
            role="status"
            aria-live="polite"
          >
            {/* Backdrop dimmer — light so tap targets remain visible. */}
            <div className="absolute inset-0 bg-slate-900/25 backdrop-blur-[1px]" />
            {/* Confetti bits */}
            {Array.from({ length: 26 }).map((_, i) => (
              <motion.span
                key={i}
                aria-hidden
                initial={{ y: -30, x: (Math.random() - 0.5) * 300, opacity: 1, rotate: 0 }}
                animate={{ y: 500, opacity: 0, rotate: Math.random() * 720 }}
                transition={{ duration: 1.8 + Math.random() * 1.2, delay: Math.random() * 0.4, ease: "easeOut" }}
                className="absolute top-8 text-2xl md:text-3xl"
                style={{ left: `${5 + Math.random() * 90}%` }}
              >
                {["🎉","🎊","⭐","✨","🌟","🪙","🏆"][i % 7]}
              </motion.span>
            ))}
            <motion.div
              initial={{ scale: 0.6, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 16 }}
              className="relative rounded-3xl border-4 border-slate-900 bg-yellow-200 px-6 py-5 max-w-[85vw] text-center shadow-[8px_8px_0_0_rgba(15,23,42,1)]"
            >
              <p className="text-6xl md:text-7xl" aria-hidden>{MILESTONE_LABEL[burst.milestone].emoji}</p>
              <p className="mt-2 font-heading text-2xl md:text-3xl font-bold">{MILESTONE_LABEL[burst.milestone].title}</p>
              <p className="mt-1 text-xs md:text-sm font-bold text-slate-700">{MILESTONE_LABEL[burst.milestone].blurb}</p>
              <p className="mt-2 inline-block px-3 py-1 rounded-full border-[2.5px] border-slate-900 bg-white font-heading text-sm md:text-base font-bold shadow-[0_3px_0_0_rgba(15,23,42,1)]">
                🎯 {burst.total} pts today
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {recap && (
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed z-[55] left-2 right-2 rounded-3xl border-[3px] border-slate-900 bg-emerald-200 shadow-[0_-6px_18px_-4px_rgba(15,23,42,0.28)] p-3 md:p-4"
            style={{ bottom: "calc(80px + env(safe-area-inset-bottom))" }}
            data-testid="daily-recap-card"
            role="dialog"
            aria-label="Yesterday's recap"
          >
            <button
              type="button"
              onClick={dismissRecap}
              data-testid="daily-recap-dismiss"
              aria-label="Dismiss recap"
              className="absolute top-2 right-2 h-8 w-8 inline-flex items-center justify-center rounded-full border-[2.5px] border-slate-900 bg-white hover:bg-slate-100 transition-colors"
            >
              <X size={14} strokeWidth={3} />
            </button>
            <div className="pr-8">
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Yesterday</p>
              <p className="mt-0.5 font-heading text-base md:text-lg font-bold leading-tight">
                You earned {recap.total} points across {recap.activities?.length || 0} activities!
              </p>
              <p className="mt-1 text-xs md:text-sm font-bold text-slate-700">
                {(() => {
                  const streak = getDailyStreak();
                  if (streak >= 2) return `🔥 ${streak}-day streak — keep it going today!`;
                  if (recap.total >= 25) return "🌟 You hit your Daily Star yesterday. Do it again!";
                  return "Log in every day to build a streak — 25+ pts a day = 🔥";
                })()}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
